import { useState } from 'react';
import './ManagerForm.css';

const USERNAME_RE = /^[a-zA-Z0-9]+$/;
const USERNAME_MAX = 20;

export function validateUsername(username) {
	if (!username) return 'Enter a username.';
	if (username.length > USERNAME_MAX)
		return `Username can be at most ${USERNAME_MAX} characters.`;
	if (!USERNAME_RE.test(username))
		return 'Username can contain letters and numbers only.';
	return null;
}

/*
 * Replaces manager.html:1-32.
 *
 * The original raised three window.alert()s for validation and save
 * failures (controllers.js:498, 596, 600); those are inline messages here,
 * wired to the fields with aria-describedby.
 */
export default function ManagerForm({
	initialManagerId = '',
	initialUsername = '',
	onLoad,
	onSave,
	loading,
	saveState,
	shareUrl,
}) {
	const [managerId, setManagerId] = useState(initialManagerId);
	const [username, setUsername] = useState(initialUsername);
	const [errors, setErrors] = useState({});

	const submitLoad = (event) => {
		event.preventDefault();
		if (!managerId && !username) {
			setErrors({ managerId: 'Enter an FPL ID or a saved username.' });
			return;
		}
		setErrors({});
		onLoad({ managerId: managerId || undefined, username: managerId ? undefined : username });
	};

	const submitSave = () => {
		const next = {};
		if (!managerId) next.managerId = 'An FPL ID is required to save a username.';
		const usernameError = validateUsername(username);
		if (usernameError) next.username = usernameError;

		setErrors(next);
		if (Object.keys(next).length > 0) return;
		onSave({ managerId, username });
	};

	return (
		<form className="manager-form" onSubmit={submitLoad} noValidate>
			<div className="manager-form__fields">
				<div className="field">
					<label className="field__label" htmlFor="managerId">
						FPL ID
					</label>
					<input
						id="managerId"
						className="field__input numeric"
						type="number"
						inputMode="numeric"
						placeholder="e.g. 1234567"
						value={managerId}
						onChange={(e) => setManagerId(e.target.value)}
						aria-invalid={Boolean(errors.managerId)}
						aria-describedby={errors.managerId ? 'managerId-error' : undefined}
					/>
					{errors.managerId && (
						<p className="field__error" id="managerId-error">
							{errors.managerId}
						</p>
					)}
				</div>

				<div className="field">
					<label className="field__label" htmlFor="username">
						Username <span className="field__hint">optional</span>
					</label>
					<input
						id="username"
						className="field__input"
						type="text"
						maxLength={USERNAME_MAX}
						placeholder="For a shareable link"
						value={username}
						onChange={(e) => setUsername(e.target.value)}
						aria-invalid={Boolean(errors.username)}
						aria-describedby={errors.username ? 'username-error' : undefined}
					/>
					{errors.username && (
						<p className="field__error" id="username-error">
							{errors.username}
						</p>
					)}
				</div>
			</div>

			<div className="manager-form__actions">
				<button className="btn btn--primary" type="submit" disabled={loading}>
					{loading ? 'Loading…' : 'Load team'}
				</button>
				<button
					className="btn btn--secondary"
					type="button"
					onClick={submitSave}
					disabled={saveState?.status === 'saving'}
				>
					{saveState?.status === 'saving' ? 'Saving…' : 'Save username'}
				</button>
			</div>

			{saveState?.status === 'error' && (
				<p className="manager-form__message manager-form__message--error" role="alert">
					{saveState.message}
				</p>
			)}

			{shareUrl && (
				<p className="manager-form__message" role="status">
					Shareable link:{' '}
					<a href={shareUrl}>{shareUrl.replace(/^https?:\/\//, '')}</a>
				</p>
			)}
		</form>
	);
}
